import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";

/* ─── Animations (scoped with pr- prefix) ─── */
const STYLE = `
  @keyframes pr-card-enter {
    from { opacity:0; transform: translateY(24px) scale(0.97); }
    to   { opacity:1; transform: translateY(0) scale(1); }
  }
  @keyframes pr-mode-in {
    from { opacity:0; transform: translateY(7px); }
    to   { opacity:1; transform: translateY(0); }
  }
  @keyframes pr-soft-shake {
    0%,100% { transform:translateX(0); }
    15%  { transform:translateX(-8px); }
    30%  { transform:translateX(6px); }
    45%  { transform:translateX(-4px); }
    60%  { transform:translateX(2px); }
    75%  { transform:translateX(-1px); }
  }
  @keyframes pr-char-in {
    from { opacity:0; transform:translateY(-6px) scale(0.6); }
    to   { opacity:1; transform:translateY(0) scale(1); }
  }
  @keyframes pr-caret-blink {
    0%,100% { opacity:1; }
    50%     { opacity:0; }
  }
  .pr-card-enter { animation: pr-card-enter 0.45s cubic-bezier(0.34,1.4,0.64,1) forwards; }
  .pr-mode-in    { animation: pr-mode-in   0.28s cubic-bezier(0.2,0.9,0.2,1) forwards; }
  .pr-shake      { animation: pr-soft-shake 0.6s cubic-bezier(.36,.07,.19,.97) both; }
  .pr-char-in    { animation: pr-char-in   220ms cubic-bezier(0.34,1.56,0.64,1) both; }
  .pr-caret      { animation: pr-caret-blink 1s step-end infinite; }
  @keyframes pr-arm-l-in{0%{transform:translate(-10px,11px) rotate(-50deg) scale(.88);opacity:0}100%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}}
  @keyframes pr-arm-r-in{0%{transform:translate(10px,11px) rotate(50deg) scale(.88);opacity:0}100%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}}
  @keyframes pr-arm-l-out{0%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}100%{transform:translate(-10px,11px) rotate(-50deg) scale(.88);opacity:0}}
  @keyframes pr-arm-r-out{0%{transform:translate(0,0) rotate(0deg) scale(1);opacity:1}100%{transform:translate(10px,11px) rotate(50deg) scale(.88);opacity:0}}
  @media (prefers-reduced-motion: reduce) {
    .pr-card-enter, .pr-mode-in, .pr-shake, .pr-char-in {
      animation: none !important;
      opacity: 1 !important;
      transform: none !important;
    }
    .pr-caret { animation: none !important; opacity: 1 !important; }
  }
`;

/* ─── FloatingInput — notched outline + per-char animation ─── */
function FloatingInput({ label, value, onChange, type = "text", id, autoComplete, required, error = false, mono = false, children }) {
  const [focused, setFocused] = useState(false);
  const floated = focused || Boolean(value);
  const isPassword = type === "password";
  const borderColor = focused ? "#3b82f6" : error ? "#f87171" : "#e2e8f0";

  const uidRef     = useRef(0);
  const charsRef   = useRef([]);
  const prevLenRef = useRef(0);
  const [chars, setChars] = useState([]);

  useEffect(() => {
    const display = isPassword ? "•".repeat(value.length) : value;
    const prev    = charsRef.current;
    const adding  = display.length === prevLenRef.current + 1;
    const next = display.split("").map((ch, i) => {
      if (i < prev.length && prev[i].ch === ch) return prev[i];
      return { ch, uid: ++uidRef.current, fresh: adding && i >= prevLenRef.current };
    });
    charsRef.current   = next;
    prevLenRef.current = display.length;
    setChars(next);
    if (adding) {
      const t = setTimeout(() => {
        charsRef.current = charsRef.current.map(c => ({ ...c, fresh: false }));
        setChars([...charsRef.current]);
      }, 280);
      return () => clearTimeout(t);
    }
  }, [value, isPassword]);

  return (
    <div className="relative mt-2">
      <fieldset
        className="absolute inset-0 rounded-2xl pointer-events-none m-0 p-0 transition-colors duration-200"
        style={{ border: `1px solid ${borderColor}`, padding: 0, margin: 0 }}
      >
        <legend style={{
          marginLeft: 12, padding: "0 4px", height: 0, overflow: "hidden",
          display: "block", whiteSpace: "nowrap", visibility: "hidden",
          fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
          maxWidth: floated ? 999 : 0, overflow: "hidden", transition: "max-width 200ms ease",
        }}>
          {` ${label} `}
        </legend>
      </fieldset>

      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoComplete={autoComplete}
        required={required}
        placeholder=""
        className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-text rounded-2xl"
        style={{ caretColor: "transparent" }}
      />

      <div className={`w-full h-14 ${children ? "pr-14" : "pr-4"} pl-4 flex items-center rounded-2xl bg-white dark:bg-transparent pointer-events-none select-none overflow-hidden transition-all duration-200 ${focused ? "ring-4 ring-blue-500/[0.07]" : ""}`}>
        <div
          className={`flex items-center text-slate-900 dark:text-slate-100 text-base leading-none ${mono ? "font-mono tracking-widest" : ""}`}
          style={{ marginTop: floated ? 10 : 0, transition: "margin-top 200ms ease" }}
        >
          {chars.map(({ ch, uid, fresh }) => (
            <span key={uid} className={fresh ? "pr-char-in" : ""} style={{ display: "inline-block" }}>{ch}</span>
          ))}
          {focused && (
            <span className="pr-caret" style={{
              display: "inline-block", width: 1.5, minWidth: 1.5,
              height: "1.15em", background: "#3b82f6",
              marginLeft: chars.length ? 1 : 0,
              verticalAlign: "middle", borderRadius: 1,
            }} />
          )}
        </div>
      </div>

      <label
        htmlFor={id}
        className={`absolute pointer-events-none select-none transition-all duration-200 z-10 ${
          floated
            ? `top-0 -translate-y-1/2 text-[11px] font-bold uppercase tracking-widest px-1 bg-transparent ${focused ? "text-blue-500 dark:text-blue-400" : error ? "text-red-400" : "text-slate-400 dark:text-slate-500"}`
            : "top-1/2 -translate-y-1/2 text-sm leading-none text-slate-400 dark:text-slate-500"
        }`}
        style={{ left: 12 }}
      >
        {label}
      </label>

      {children}
    </div>
  );
}

/* ─── Monkey password toggle ─── */
function MonkeyToggle({ shown, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={shown ? "Скрыть пароль" : "Показать пароль"}
      aria-pressed={shown}
      className="absolute right-3 top-1/2 -translate-y-1/2 z-30 h-10 w-10 flex items-center justify-center rounded-full outline-none transition-transform active:scale-90"
    >
      <span className="relative block h-8 w-8 opacity-80 hover:opacity-100 transition-opacity">
        <svg viewBox="0 0 64 64" className="h-8 w-8">
          <ellipse cx="32" cy="34" rx="20" ry="18" fill="#9a613d" />
          <ellipse cx="32" cy="36.5" rx="12.5" ry="10.5" fill="#e0b494" />
          <circle cx="14" cy="22" r="8.1" fill="#8b5637" /><circle cx="50" cy="22" r="8.1" fill="#8b5637" />
          <circle cx="14" cy="22" r="4.5" fill="#dca88a" /><circle cx="50" cy="22" r="4.5" fill="#dca88a" />
          <ellipse cx="26" cy="31" rx="2.8" ry={shown ? 3.1 : 1.1} fill="#1f2937" style={{ transition: "all 460ms ease" }} />
          <ellipse cx="38" cy="31" rx="2.8" ry={shown ? 3.1 : 1.1} fill="#1f2937" style={{ transition: "all 460ms ease" }} />
          <ellipse cx="32" cy="38.9" rx="2.1" ry="1.5" fill="#7c4f32" />
          <path d="M27.5 43c2.1 2 6.9 2 9 0" stroke="#6b4228" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        </svg>
        <span className="absolute left-[-8px] top-[10px]" style={{ opacity: shown ? 0 : 1, animation: shown ? "pr-arm-l-out 520ms ease forwards" : "pr-arm-l-in 520ms ease forwards" }}>
          <svg viewBox="0 0 24 28" className="h-[28px] w-[24px]"><path d="M17.2 5.6 C11 7.2, 5 12.5, 4.2 18.4 C3.8 21.6, 6.2 24, 9.6 24 C12.2 24, 14.2 23, 15.7 21.2" stroke="#c18457" strokeWidth="1" fill="none" /><circle cx="18" cy="4.8" r="5.4" fill="#c18457" /><circle cx="18" cy="4.8" r="3.3" fill="#e4b796" /></svg>
        </span>
        <span className="absolute right-[-8px] top-[10px]" style={{ opacity: shown ? 0 : 1, animation: shown ? "pr-arm-r-out 520ms ease forwards" : "pr-arm-r-in 520ms ease forwards" }}>
          <svg viewBox="0 0 24 28" className="h-[28px] w-[24px]"><path d="M6.8 5.6 C13 7.2, 19 12.5, 19.8 18.4 C20.2 21.6, 17.8 24, 14.4 24 C11.8 24, 9.8 23, 8.3 21.2" stroke="#c18457" strokeWidth="1" fill="none" /><circle cx="6" cy="4.8" r="5.4" fill="#c18457" /><circle cx="6" cy="4.8" r="3.3" fill="#e4b796" /></svg>
        </span>
      </span>
    </button>
  );
}

/* ─── Submit button ─── */
function SubmitButton({ busy, children }) {
  return (
    <button
      disabled={busy}
      className="w-full h-14 rounded-2xl bg-[#3b82f6] hover:bg-[#1e3a8a] text-white font-bold text-[16px] active:scale-[0.97] transition-all duration-200 disabled:opacity-50 flex items-center justify-center"
    >
      {busy
        ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        : children}
    </button>
  );
}

/* ─── Auto-redirect helper ─── */
function RedirectAfter({ delay, to }) {
  useEffect(() => {
    const t = setTimeout(() => { window.location.href = to; }, delay);
    return () => clearTimeout(t);
  }, [delay, to]);
  return null;
}

/* ═══════════════════════════════════════════ */
export default function PasswordReset() {
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [isShaking, setIsShaking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const triggerError = (msg) => {
    setErr(msg); setIsShaking(true);
    setTimeout(() => setIsShaking(false), 600);
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${window.location.origin}/auth/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = res.ok ? await res.json() : {};
      setEmailSent(Boolean(data.email_sent));
      setStep("confirm");
    } catch {
      triggerError("Не удалось отправить код. Попробуйте позже.");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
setBusy(true); setErr("");
    try {
      const res = await fetch(`${window.location.origin}/auth/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          token: code.trim().toUpperCase(),
          new_password: newPassword,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setStep("done");
      } else {
        const detail = typeof data.detail === "string"
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail.map((e) => e.msg || String(e)).join(", ")
            : "Неверный код или email";
        triggerError(detail);
      }
    } catch {
      triggerError("Ошибка при смене пароля");
    } finally {
      setBusy(false);
    }
  };

  /* ──── SUCCESS ──── */
  if (step === "done") {
    return (
      <div className="h-[100dvh] flex items-center justify-center px-4 py-4 overflow-y-auto overscroll-contain">
        <style>{STYLE}</style>
        <div className="pr-card-enter w-full max-w-[420px]">
          <div className="bg-white dark:bg-[#2a2c32] border border-slate-200/80 dark:border-slate-700/40 shadow-[0_15px_40px_rgba(0,0,0,0.06)] dark:shadow-[0_15px_40px_rgba(0,0,0,0.4)] rounded-[2.5rem] px-8 py-10 md:px-10 text-center space-y-5">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <svg className="h-7 w-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <div>
              <h1 className="text-[22px] font-bold text-slate-900 dark:text-slate-100 tracking-tight">Пароль изменён</h1>
              <p className="text-[13px] text-slate-400 dark:text-slate-500 mt-2">Перенаправляем на страницу входа...</p>
            </div>
            <Link to="/login" className="inline-block text-[13px] font-semibold text-blue-500 hover:text-blue-700 transition-colors">
              Войти сейчас
            </Link>
          </div>
        </div>
        <RedirectAfter delay={2000} to="/login" />
      </div>
    );
  }

  const title = step === "request" ? "Сброс пароля" : "Новый пароль";
  const subtitle = step === "request"
    ? "Отправим код подтверждения на вашу почту"
    : emailSent
      ? `Код отправлен на ${email}`
      : `Если аккаунт с почтой ${email} существует, код отправлен`;

  return (
    <div className="h-[100dvh] flex items-center justify-center px-4 py-4 overflow-y-auto overscroll-contain">
      <style>{STYLE}</style>
      <div className="pr-card-enter w-full max-w-[420px]">
        <div className={`bg-white dark:bg-[#2a2c32] border border-slate-200/80 dark:border-slate-700/40 shadow-[0_15px_40px_rgba(0,0,0,0.06)] dark:shadow-[0_15px_40px_rgba(0,0,0,0.4)] rounded-[2.5rem] px-8 py-10 md:px-10 ${isShaking ? "pr-shake" : ""}`}>

          <h1 key={title} className="pr-mode-in text-[26px] font-bold text-slate-900 dark:text-slate-100 text-center mb-1 tracking-tight">
            {title}
          </h1>
          <p key={subtitle} className="pr-mode-in text-[13px] text-slate-400 dark:text-slate-500 text-center mb-7">
            {subtitle}
          </p>

          <form onSubmit={step === "request" ? handleRequest : handleConfirm} className="space-y-4">
            {step === "request" ? (
              <FloatingInput
                id="reset-email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                autoComplete="email"
                required
              />
            ) : (
              <>
                <FloatingInput
                  id="reset-code"
                  label="Код из письма"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  autoComplete="one-time-code"
                  required
                  mono
                />
                <FloatingInput
                  id="reset-password"
                  label="Новый пароль"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                >
                  <MonkeyToggle shown={showPassword} onClick={() => setShowPassword((v) => !v)} />
                </FloatingInput>
                {newPassword.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 pt-1">
                    <span className={`text-[12px] flex items-center gap-1 transition-colors ${newPassword.length >= 8 ? "text-emerald-600" : "text-slate-400 dark:text-slate-500"}`}>
                      <span>{newPassword.length >= 8 ? "✓" : "○"}</span> 8+ символов
                    </span>
                    <span className={`text-[12px] flex items-center gap-1 transition-colors ${/\d/.test(newPassword) ? "text-emerald-600" : "text-slate-400 dark:text-slate-500"}`}>
                      <span>{/\d/.test(newPassword) ? "✓" : "○"}</span> цифра
                    </span>
                    <span className={`text-[12px] flex items-center gap-1 transition-colors ${/[A-ZА-ЯЁ]/.test(newPassword) ? "text-emerald-600" : "text-slate-300 dark:text-slate-600"}`}>
                      <span>{/[A-ZА-ЯЁ]/.test(newPassword) ? "✓" : "○"}</span> заглавная
                    </span>
                    <span className={`text-[12px] flex items-center gap-1 transition-colors ${/[^A-Za-zА-ЯЁа-яё0-9]/.test(newPassword) ? "text-emerald-600" : "text-slate-300 dark:text-slate-600"}`}>
                      <span>{/[^A-Za-zА-ЯЁа-яё0-9]/.test(newPassword) ? "✓" : "○"}</span> спецсимвол
                    </span>
                  </div>
                )}
              </>
            )}

            <div role="alert" aria-live="polite">
              {err && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-red-50/80 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40">
                  <svg className="h-4 w-4 text-red-400 flex-shrink-0" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-red-600 dark:text-red-400 text-[13px] font-semibold">{err}</span>
                </div>
              )}
            </div>

            <div className="pt-1 space-y-3">
              <SubmitButton busy={busy}>
                {step === "request" ? "Отправить код" : "Сменить пароль"}
              </SubmitButton>

              {step === "confirm" ? (
                <button
                  type="button"
                  onClick={() => { setStep("request"); setErr(""); }}
                  className="w-full text-[13px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  ← Назад
                </button>
              ) : (
                <p className="text-[13px] text-center text-slate-500 dark:text-slate-400">
                  <Link to="/login" className="font-bold text-blue-600 hover:text-blue-800 transition-colors">
                    ← Вернуться ко входу
                  </Link>
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
